"use client";

import type { CSSProperties } from "react";
import { cn } from "@/lib/utils";
import type { Participant } from "../base-match-template";
import {
  FieldMatchFrame,
  type FieldMatchState,
  FieldScore,
  PerspectivePlane,
  type PlaneSpec,
  projectOnPlane,
} from "./field-sport-scene-kit";

export type SnookerTablePreviewProps = {
  yourTeam: Participant[];
  opponentTeam: Participant[];
  yourWon?: boolean;
  matchScore?: { yourFrames: number; opponentFrames: number };
  frames?: { yourScore: number; opponentScore: number }[];
  highestBreak?: number;
};

// The table, seen from the players' end under its lamp. The plane is the
// whole table top (rails included); the cloth sits inset by the rail width.
const TABLE: PlaneSpec = {
  top: 54,
  depth: 168,
  tilt: 50,
  perspective: 640,
  farWidth: 76,
};
const RAIL = 12; // px, in plane coordinates
const CLOTH_MID = TABLE.depth / 2;

/** Ball diameter: ~2.5cqw, clamped so it reads at 301px and stays sane at 600. */
const BALL = "clamp(7px, 2.5cqw, 12px)";

const BALL_COLORS = {
  red: ["#ff6b6b", "#d01818", "#5c0505"],
  yellow: ["#fff7a8", "#f2c21b", "#7a5600"],
  green: ["#8ff0a9", "#16884a", "#04331a"],
  brown: ["#e5ae7f", "#8a4b1d", "#3a1a06"],
  blue: ["#9cc7ff", "#1f5fd1", "#071f57"],
  pink: ["#ffd3e6", "#f272aa", "#7a1f48"],
  black: ["#8a8a8a", "#1c1c1c", "#000000"],
  cue: ["#ffffff", "#f1efe6", "#9d9a8e"],
} as const;

type BallColor = keyof typeof BALL_COLORS;

function SnookerBall({
  color,
  style,
  className,
}: {
  color: BallColor;
  style?: CSSProperties;
  className?: string;
}) {
  const [hi, mid, lo] = BALL_COLORS[color];
  return (
    <div
      className={cn(
        "absolute -translate-x-1/2 -translate-y-1/2 rounded-full",
        className,
      )}
      style={{
        width: BALL,
        height: BALL,
        background: `radial-gradient(circle at 34% 28%, #ffffff 0 9%, ${hi} 16%, ${mid} 52%, ${lo} 100%)`,
        boxShadow: "1px 3px 4px rgba(0,0,0,0.55)",
        ...style,
      }}
    />
  );
}

/** A ball resting on a spot of the cloth (u across, v into depth). */
function TableBall({
  color,
  u,
  v,
}: {
  color: BallColor;
  u: number;
  v: number;
}) {
  const p = projectOnPlane(TABLE, u, v);
  return <SnookerBall color={color} style={{ left: p.left, top: p.top }} />;
}

/**
 * The fifteen reds racked as a triangle, apex towards the pink. Laid out in
 * ball diameters around the projected rack centre — at this size perspective
 * across the rack is negligible.
 */
function RedRack({ u, v }: { u: number; v: number }) {
  const p = projectOnPlane(TABLE, u, v);
  const balls: Array<{ col: number; row: number }> = [];
  for (let col = 0; col < 5; col++) {
    for (let row = 0; row <= col; row++) balls.push({ col, row });
  }
  return (
    <div className="absolute" style={{ left: p.left, top: p.top }}>
      {balls.map(({ col, row }) => (
        <SnookerBall
          key={`${col}-${row}`}
          color="red"
          style={{
            left: `calc(${BALL} * ${(col - 2) * 0.87})`,
            top: `calc(${BALL} * ${row - col / 2} * 0.82)`,
          }}
        />
      ))}
    </div>
  );
}

/** Cloth, cushions, pockets and rails, flat in plane coordinates. */
function TableTop() {
  const pockets: Array<{ u: string; v: string; mid?: boolean }> = [
    { u: "0%", v: "0%" },
    { u: "50%", v: "0%", mid: true },
    { u: "100%", v: "0%" },
    { u: "0%", v: "100%" },
    { u: "50%", v: "100%", mid: true },
    { u: "100%", v: "100%" },
  ];
  return (
    <>
      {/* Mahogany rails: grain, lacquer highlight, a bevelled outer edge */}
      <div className="absolute inset-0 rounded-[14px] bg-[linear-gradient(180deg,#6b2c14_0%,#4a1c0b_45%,#3a1408_100%)] shadow-[inset_0_1px_0_rgba(255,190,140,0.35),inset_0_-2px_0_rgba(0,0,0,0.5)]" />
      <div className="absolute inset-0 rounded-[14px] [background-image:repeating-linear-gradient(90deg,rgba(255,170,110,0.07)_0px,rgba(255,170,110,0.07)_2px,transparent_2px,transparent_7px,rgba(0,0,0,0.12)_7px,rgba(0,0,0,0.12)_8px,transparent_8px,transparent_13px)] opacity-60" />
      {/* Diamond inlays */}
      {[0.25, 0.75].map((u) => (
        <div key={`d-top-${u}`}>
          <div
            className="absolute top-[4px] h-[3px] w-[3px] -translate-x-1/2 rotate-45 bg-[#f5e3c0]/80"
            style={{ left: `${u * 100}%` }}
          />
          <div
            className="absolute bottom-[4px] h-[3px] w-[3px] -translate-x-1/2 rotate-45 bg-[#f5e3c0]/80"
            style={{ left: `${u * 100}%` }}
          />
        </div>
      ))}

      {/* Cloth: worsted baize with a nap, brightest under the lamp */}
      <div
        className="absolute overflow-hidden rounded-[4px] bg-[radial-gradient(ellipse_60%_70%_at_50%_45%,#2f9a52_0%,#1d7a3c_45%,#105228_100%)] shadow-[inset_0_0_0_3px_rgba(8,48,22,0.95),inset_0_4px_10px_rgba(0,0,0,0.45)]"
        style={{ inset: RAIL }}
      >
        <div className="absolute inset-0 [background-image:repeating-linear-gradient(90deg,rgba(255,255,255,0.025)_0px,rgba(255,255,255,0.025)_1px,transparent_1px,transparent_3px)] opacity-50" />
        {/* Baulk line and D */}
        <div className="absolute top-0 bottom-0 left-[20%] w-px bg-white/55" />
        <div className="absolute top-1/2 left-[20%] h-[34%] w-[11%] -translate-x-full -translate-y-1/2 rounded-l-full border border-r-0 border-white/55" />
        {/* Spots */}
        {[0.5, 0.74, 0.91].map((u) => (
          <div
            key={u}
            className="absolute top-1/2 h-[2px] w-[2px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/60"
            style={{ left: `${u * 100}%` }}
          />
        ))}
      </div>

      {/* Pockets: leather-lined, with a brass fall */}
      {pockets.map((pocket) => (
        <div
          key={`${pocket.u}-${pocket.v}`}
          className={cn(
            "absolute -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,#000_0%,#000_55%,#3a2410_62%,#b98a3e_74%,#5a3a14_100%)]",
            pocket.mid ? "h-[15px] w-[15px]" : "h-[19px] w-[19px]",
          )}
          style={{
            left: `calc(${pocket.u} + ${pocket.u === "0%" ? RAIL * 0.6 : pocket.u === "100%" ? -RAIL * 0.6 : 0}px)`,
            top: `calc(${pocket.v} + ${pocket.v === "0%" ? RAIL * 0.6 : -RAIL * 0.6}px)`,
          }}
        />
      ))}
    </>
  );
}

function SnookerRoom() {
  return (
    <>
      {/* Club room: dark panelled walls, warm falloff */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_75%_90%_at_50%_45%,#2a1a10_0%,#140c07_55%,#070403_100%)]" />
      <div className="absolute inset-x-0 top-0 h-[60px] [background-image:repeating-linear-gradient(90deg,transparent_0px,transparent_36px,rgba(0,0,0,0.5)_36px,rgba(0,0,0,0.5)_38px,rgba(255,200,150,0.05)_38px,rgba(255,200,150,0.05)_39px)] opacity-60" />

      {/* Table shadow on the floor */}
      <div className="absolute inset-x-[4%] top-[70px] bottom-[-6px] rounded-[50%] bg-[radial-gradient(closest-side,rgba(0,0,0,0.65),transparent)]" />

      <PerspectivePlane plane={TABLE}>
        <TableTop />
      </PerspectivePlane>

      {/* The lamp: a long hood across the top and its warm pool of light */}
      <div
        className="absolute inset-x-0 top-0 bottom-0 bg-[linear-gradient(180deg,rgba(255,226,170,0.2)_0%,rgba(255,226,170,0.05)_70%,transparent)]"
        style={{
          clipPath: "polygon(22% 10px, 78% 10px, 96% 100%, 4% 100%)",
        }}
      />
      <div className="absolute top-0 right-[20%] left-[20%] h-[11px] rounded-b-[6px] bg-[linear-gradient(180deg,#0e2a17_0%,#1d4a2a_60%,#0b1f11_100%)] shadow-[0_3px_0_rgba(255,232,180,0.55),0_8px_22px_rgba(255,220,150,0.45)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_48%_42%_at_50%_52%,rgba(255,236,190,0.16),transparent)]" />

      {/* Edge falloff */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_85%_100%_at_50%_45%,transparent_55%,rgba(0,0,0,0.55)_100%)]" />
    </>
  );
}

export function SnookerTablePreview({
  yourTeam,
  opponentTeam,
  yourWon,
  matchScore,
  frames,
  highestBreak,
}: SnookerTablePreviewProps) {
  const opponentWon = opponentTeam.length > 0 && yourWon === false;
  const yourIsWinner = !!yourWon && !opponentWon;

  const matchState: FieldMatchState =
    yourTeam.length === 0 && opponentTeam.length === 0
      ? "EMPTY"
      : matchScore &&
          (matchScore.yourFrames > 0 ||
            matchScore.opponentFrames > 0 ||
            highestBreak)
        ? "FULL"
        : "NO_SCORE";

  const totalFrames = matchScore
    ? matchScore.yourFrames + matchScore.opponentFrames
    : 0;
  const recentFrames = (frames ?? [])
    .map((frame, n) => ({ ...frame, id: `frame-${n + 1}` }))
    .slice(-6);

  return (
    <FieldMatchFrame
      label="Snooker"
      frameClassName="bg-[#0c0705]"
      matchState={matchState}
      scene={<SnookerRoom />}
      yourTeam={yourTeam}
      opponentTeam={opponentTeam}
      yourWinner={yourIsWinner}
      opponentWinner={opponentWon}
      bandClassName="top-[44px] bottom-[18px]"
      decor={
        matchState === "FULL" ? (
          // Mid-frame: a few balls left out of the way of the score.
          <>
            <TableBall color="red" u={0.36} v={RAIL + 14} />
            <TableBall color="red" u={0.62} v={RAIL + 10} />
            <TableBall color="pink" u={0.84} v={TABLE.depth - RAIL - 12} />
            <TableBall color="cue" u={0.2} v={TABLE.depth - RAIL - 12} />
          </>
        ) : matchState === "EMPTY" ? (
          // Nobody at the table yet: colours on their spots, clear of "Waiting".
          <>
            <TableBall color="pink" u={0.74} v={CLOTH_MID} />
            <TableBall color="blue" u={0.4} v={CLOTH_MID - 32} />
            <TableBall color="black" u={0.66} v={RAIL + 12} />
            <TableBall color="cue" u={0.3} v={TABLE.depth - RAIL - 18} />
          </>
        ) : (
          // Ready to break: reds racked behind the pink, cue ball in the D.
          <>
            <RedRack u={0.57} v={CLOTH_MID} />
            <TableBall color="pink" u={0.47} v={CLOTH_MID} />
            <TableBall color="blue" u={0.4} v={CLOTH_MID - 32} />
            <TableBall color="black" u={0.66} v={RAIL + 12} />
            <TableBall color="cue" u={0.3} v={TABLE.depth - RAIL - 18} />
          </>
        )
      }
      center={
        matchState === "FULL" && matchScore ? (
          <>
            <FieldScore
              transitionKey={totalFrames}
              left={matchScore.yourFrames}
              right={matchScore.opponentFrames}
              leftWinner={yourIsWinner}
              rightWinner={opponentWon}
            />
            {highestBreak || recentFrames.length > 0 ? (
              <div className="flex flex-col items-center gap-1 rounded-2xl bg-black/45 px-3 py-1 ring-1 ring-white/10">
                {highestBreak ? (
                  <span className="flex items-baseline gap-1.5 text-[10px] font-bold tracking-[0.22em] whitespace-nowrap uppercase">
                    <span className="text-white/55">High break</span>
                    <span className="text-[13px] tracking-normal text-[#ffe08a] tabular-nums">
                      {highestBreak}
                    </span>
                  </span>
                ) : null}
                {recentFrames.length > 0 ? (
                  // One tick per frame: bright for yours, dim for theirs.
                  <span className="flex items-center gap-1">
                    {recentFrames.map((frame) => {
                      const yourFrame = frame.yourScore > frame.opponentScore;
                      return (
                        <span
                          key={frame.id}
                          className={cn(
                            "h-[4px] w-[10px] rounded-full",
                            yourFrame ? "bg-white/90" : "bg-white/25",
                          )}
                        />
                      );
                    })}
                  </span>
                ) : null}
              </div>
            ) : null}
          </>
        ) : null
      }
    />
  );
}
